import React from 'react';

import { Stack, TextField } from '@mui/material';

import FormSection from 'components/condition-list/FormSection';
import CustomSelect from 'components/form/CustomSelect';
import { ExpenseLabel } from 'stores/ExpenseStoreAdapter';

export type ExpenseStoreFormValues = {
  name: string;
  defaultLabelId: string;
  invoiceNumber: string;
};

type Props = {
  values: ExpenseStoreFormValues;
  onChange: (values: ExpenseStoreFormValues) => void;
  /** デフォルト品目ラベルの選択肢（有効なラベル） */
  labels: ExpenseLabel[];
  /** 参照専用（詳細表示中で未編集）か */
  disabled?: boolean;
  /** name の 422/バリデーションエラー文言 */
  nameError?: string;
  /** invoiceNumber のバリデーションエラー文言 */
  invoiceError?: string;
};

const NAME_MAX = 128;
const INVOICE_MAX = 14;

/**
 * 店舗の作成/更新フォーム（ConditionList の詳細ペイン内で使用）。
 * 店舗名（必須 max128）・デフォルト品目ラベル（任意）・インボイス番号（任意 max14・T+13桁）。
 */
const ExpenseStoreForm: React.FC<Props> = ({
  values,
  onChange,
  labels,
  disabled = false,
  nameError,
  invoiceError,
}) => {
  const labelOptions = labels.map((l) => ({ value: l.expenseLabelId, label: l.name }));

  return (
    <Stack spacing={2.5}>
      <FormSection title="店舗">
        <Stack spacing={2}>
          <TextField
            label="店舗名"
            size="small"
            fullWidth
            required
            disabled={disabled}
            value={values.name}
            onChange={(e) => onChange({ ...values, name: e.target.value })}
            error={!!nameError}
            helperText={nameError}
            inputProps={{ maxLength: NAME_MAX }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
          <CustomSelect
            mode="single"
            label="デフォルト品目ラベル"
            placeholder="未設定"
            disabled={disabled}
            options={labelOptions}
            value={values.defaultLabelId}
            onChange={(v) => onChange({ ...values, defaultLabelId: v })}
            helperText="選択すると、この店舗を選んだとき品目ラベルが自動で入ります"
          />
          <TextField
            label="インボイス番号"
            size="small"
            fullWidth
            disabled={disabled}
            value={values.invoiceNumber}
            onChange={(e) => onChange({ ...values, invoiceNumber: e.target.value })}
            error={!!invoiceError}
            helperText={invoiceError ?? 'T+13桁（任意）'}
            inputProps={{ maxLength: INVOICE_MAX }}
            placeholder="T1234567890123"
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
        </Stack>
      </FormSection>
    </Stack>
  );
};

export default ExpenseStoreForm;
